import { createFileRoute } from "@tanstack/react-router";
import { Phone, Mail, MapPin, Globe } from "lucide-react";
import * as React from "react";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";

const RECAPTCHA_SITE_KEY = "6LdobvwsAAAAANkTxLRzwXgWrqBBSGUuML_-XoNf";

declare global {
  interface Window {
    grecaptcha?: {
      ready: (cb: () => void) => void;
      execute: (siteKey: string, opts: { action: string }) => Promise<string>;
    };
  }
}
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { submitContactToGHL } from "@/lib/ghl.functions";
import { trackGenerateLead } from "@/lib/analytics";
import contactBg from "@/assets/contact-bg.png";
import { SITE_URL, canonical, seoCanonicalLink, seoOgUrl } from "@/lib/seo";

export const Route = createFileRoute("/contact")({
  component: ContactPage,
  head: () => ({
    meta: [
      { title: "Contact SimpleTech — Free IT Consultation" },
      { name: "description", content: "Get in touch with SimpleTech for a free consultation. Reach our team by phone, email, or visit us in Southern California." },
      { property: "og:title", content: "Contact SimpleTech" },
      { property: "og:description", content: "Book a free consultation with our IT specialists." },
      seoOgUrl("/contact"),
    ],
    links: [seoCanonicalLink("/contact")],
  }),
});

const contactMethods = [
  { icon: Globe, title: "Chat With Us:", value: "Speak to our friendly team via live chat." },
  { icon: Mail, title: "Email Us:", value: "contact@simpletechhelper.com" },
  { icon: Phone, title: "Call Us:", value: "Call our team Mon-Fri from 7am to 5pm.\n\n(909) 255-9112" },
];

const HELP_OPTIONS = [
  "IT Support",
  "Managed IT",
  "Phone Systems",
  "Website",
  "Software Integration",
  "Tech Support for Seniors",
  "ADA Compliant Website",
  "CRM Services",
];

function ContactPage() {
  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <SiteHeader active="contact" />
      <main id="main-content" className="flex-1">

        {/* Hero banner */}
        <section
          className="relative overflow-hidden bg-cover bg-center"
          style={{ backgroundImage: `url(${contactBg})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-[#0b0a3d]/70 via-[#0b0a3d]/55 to-[#0b0a3d]/85" aria-hidden />
          <div className="relative z-10 max-w-7xl mx-auto px-6 py-24 md:py-36 text-center text-white">
            <p className="text-sm font-bold tracking-[0.3em] text-sky-300 uppercase">Get In Touch</p>
            <h1 className="mt-4 text-4xl md:text-6xl font-extrabold tracking-tight leading-[1.05]">
              <span className="md:hidden">Contact Us Today<br />For A Free<br />Consultation!</span>
              <span className="hidden md:inline">Contact Us Today For A<br />Free Consultation!</span>
            </h1>
          </div>
        </section>

        {/* Get help + form */}
        <section className="py-20 md:py-28 bg-white">
          <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 lg:gap-20">
            {/* Left: copy + contact methods */}
            <div>
              <p className="text-sm font-bold tracking-widest text-violet-700 uppercase mb-4">
                Get The Help You Need - Fast And Easy
              </p>
              <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900 leading-tight">
                The Life-Changing<br />Power Of SimpleTech
              </h2>
              <p className="mt-6 text-slate-600 leading-relaxed max-w-xl">
                SimpleTech provides a wide range of services that can meet any need. From network and server setup to hardware information technology repairs and maintenance services, they have innovative solutions tailored specifically to businesses, tech consumers, and seniors alike. Contact us today to set up your IT Support!
              </p>

              <div className="mt-12 space-y-8">
                {contactMethods.map(({ icon: Icon, title, value }) => (
                  <div key={title} className="flex items-start gap-5">
                    <div className="w-12 h-12 rounded-full bg-sky-100 grid place-items-center shrink-0">
                      <Icon className="w-5 h-5 text-sky-500" />
                    </div>
                    <div>
                      <div className="text-lg font-extrabold text-slate-900">{title}</div>
                      <div className="text-slate-600 whitespace-pre-line">{value}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: form card */}
            <div className="bg-[#0b0a3d] rounded-2xl p-6 md:p-8 text-white shadow-xl">
              <ContactForm />
            </div>
          </div>
        </section>

        {/* Map + address */}
        <section className="bg-slate-50 border-t border-slate-200">
          <div className="max-w-7xl mx-auto px-6 py-16 grid lg:grid-cols-3 gap-10 items-center">
            <div className="lg:col-span-1">
              <p className="text-sm font-bold tracking-widest text-violet-700 uppercase mb-3">Visit Us</p>
              <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 leading-tight">
                Our Office
              </h2>
              <div className="mt-6 flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-sky-100 grid place-items-center shrink-0">
                  <MapPin className="w-5 h-5 text-sky-500" />
                </div>
              <address className="not-italic text-slate-700 leading-relaxed">
                8880 N Benson Ave #121<br />
                Montclair, CA 91763
              </address>
              <p className="text-sm text-red-500 mt-2">By appointments only</p>
              </div>
              <a
                href="https://www.google.com/maps/dir/?api=1&destination=8880+N+Benson+Ave+%23121+Montclair+CA+91763"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-8 bg-sky-600 hover:bg-sky-700 text-white font-bold tracking-wider text-sm px-6 py-3 rounded-md transition-colors"
              >
                GET DIRECTIONS
              </a>
            </div>
            <div className="lg:col-span-2 rounded-2xl overflow-hidden shadow-lg border border-slate-200">
              <iframe
                title="SimpleTech office location"
                src="https://www.google.com/maps?q=8880+N+Benson+Ave+%23121+Montclair+CA+91763&output=embed"
                width="100%"
                height="400"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

function ContactForm() {
  const submit = useServerFn(submitContactToGHL);
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    companyName: "",
    notes: "",
  });
  const [helpWith, setHelpWith] = useState<string[]>([]);
  const [smsConsent, setSmsConsent] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  // Stable per-mount timestamp used by server to detect too-fast submissions.
  const [formLoadedAt] = useState(() => Date.now());
  const recaptchaLoadedRef = React.useRef(false);

  // Lazy-load reCAPTCHA on first interaction with the form. This keeps the
  // ~750KB of Google JS off the page load entirely — Lighthouse measures
  // before the user ever focuses a field, and by the time they submit the
  // script is warm in the background.
  const loadRecaptcha = React.useCallback(() => {
    if (recaptchaLoadedRef.current) return;
    recaptchaLoadedRef.current = true;
    if (typeof window === "undefined") return;
    if (document.querySelector('script[src*="recaptcha/api.js"]')) return;
    const s = document.createElement("script");
    s.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    s.async = true;
    s.defer = true;
    document.body.appendChild(s);
  }, []);


  const update = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const toggleHelp = (opt: string) =>
    setHelpWith((arr) => (arr.includes(opt) ? arr.filter((x) => x !== opt) : [...arr, opt]));

  const getCaptchaToken = (): Promise<string> => {
    // Ensure script is loading (covers edge case where user never focused a field).
    loadRecaptcha();
    return new Promise((resolve, reject) => {
      const start = Date.now();
      const tryExecute = () => {
        if (window.grecaptcha) {
          window.grecaptcha.ready(() => {
            window.grecaptcha!
              .execute(RECAPTCHA_SITE_KEY, { action: "contact_submit" })
              .then(resolve)
              .catch(reject);
          });
          return;
        }
        if (Date.now() - start > 8000) {
          reject(new Error("reCAPTCHA failed to load. Please refresh and try again."));
          return;
        }
        setTimeout(tryExecute, 150);
      };
      tryExecute();
    });
  };


  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("submitting");
    setErrorMsg("");
    try {
      const captchaToken = await getCaptchaToken();
      const payload = {
        ...form,
        helpWith,
        phone: form.phone.startsWith("+") ? form.phone : `+1${form.phone}`,
        captchaToken,
        website: honeypot,
        formLoadedAt,
      };
      const res = await submit({ data: payload });
      if (res?.success) {
        // Fires only after the submission actually succeeded.
        trackGenerateLead("contact");
        setStatus("success");
        setForm({ firstName: "", lastName: "", phone: "", email: "", companyName: "", notes: "" });
        setHelpWith([]);
        setSmsConsent(false);
      } else {
        setStatus("error");
        setErrorMsg(res?.error || "Something went wrong. Please try again.");
      }
    } catch (err: any) {
      setStatus("error");
      setErrorMsg(err?.message || "Network error. Please try again.");
    }
  };


  const inputCls =
    "w-full rounded-md bg-white/10 border border-white/20 px-4 py-3 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent";
  const labelCls = "block text-sm font-semibold text-white mb-1.5";

  if (status === "success") {
    return (
      <div className="py-16 text-center">
        <h3 className="text-2xl font-extrabold mb-3">Thanks — we got it!</h3>
        <p className="text-white/80">A SimpleTech specialist will be in touch shortly.</p>
        <button
          onClick={() => setStatus("idle")}
          className="mt-6 inline-block bg-sky-600 hover:bg-sky-700 text-white font-bold px-6 py-3 rounded-md transition-colors"
        >
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} onFocusCapture={loadRecaptcha} className="space-y-4">
      {/* Hide the floating reCAPTCHA badge — disclosure text below is the Google-approved alternative. */}
      <style>{`.grecaptcha-badge { visibility: hidden !important; }`}</style>

      {/* Honeypot — hidden from humans, irresistible to bots. */}
      <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", top: "auto", width: "1px", height: "1px", overflow: "hidden" }}>
        <label htmlFor="website">Website (leave blank)</label>
        <input
          id="website"
          name="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
        />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">

        <div>
          <label htmlFor="firstName" className={labelCls}>First Name *</label>
          <input id="firstName" required value={form.firstName} onChange={update("firstName")} className={inputCls} placeholder="First Name" />
        </div>
        <div>
          <label htmlFor="lastName" className={labelCls}>Last Name *</label>
          <input id="lastName" required value={form.lastName} onChange={update("lastName")} className={inputCls} placeholder="Last Name" />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="phone" className={labelCls}>Phone *</label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50 font-medium select-none">+1</span>
            <input id="phone" type="tel" required value={form.phone} onChange={update("phone")} className={`${inputCls} pl-10`} placeholder="(555) 123-4567" />
          </div>
        </div>
        <div>
          <label htmlFor="email" className={labelCls}>Email *</label>
          <input id="email" type="email" required value={form.email} onChange={update("email")} className={inputCls} placeholder="Email" />
        </div>
      </div>

      <div>
        <label htmlFor="companyName" className={labelCls}>Business Name *</label>
        <input id="companyName" required value={form.companyName} onChange={update("companyName")} className={inputCls} placeholder="Business Name" />
      </div>

      <div>
        <span className={labelCls}>What do you need help with?</span>
        <div className="grid sm:grid-cols-2 gap-2 mt-1">
          {HELP_OPTIONS.map((opt) => {
            const checked = helpWith.includes(opt);
            return (
              <label
                key={opt}
                className={`flex items-center gap-2 rounded-md border px-3 py-2 cursor-pointer transition-colors ${
                  checked ? "bg-sky-400/20 border-sky-400" : "bg-white/5 border-white/20 hover:bg-white/10"
                }`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleHelp(opt)}
                  className="accent-sky-400"
                />
                <span className="text-sm">{opt}</span>
              </label>
            );
          })}
        </div>
      </div>

      <div>
        <label htmlFor="notes" className={labelCls}>Any other information you'd like us to know?</label>
        <textarea id="notes" rows={4} value={form.notes} onChange={update("notes")} className={inputCls} placeholder="Tell us a bit more..." />
      </div>

      <p className="text-[11px] text-white/50 text-center">
        Protected by reCAPTCHA — Google{" "}
        <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-white">Privacy</a>{" "}
        &amp;{" "}
        <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-white">Terms</a>.
      </p>

      {status === "error" && (
        <p className="text-red-300 text-sm">{errorMsg}</p>
      )}

      <div>
        <label htmlFor="smsConsent" className="flex items-start gap-3 rounded-md border border-white/20 bg-white/5 px-3 py-3 cursor-pointer">
          <input
            id="smsConsent"
            name="smsConsent"
            type="checkbox"
            checked={smsConsent}
            onChange={(e) => setSmsConsent(e.target.checked)}
            className="accent-sky-400 mt-0.5 shrink-0"
          />
          <span className="text-xs text-white/80 leading-relaxed">
            I agree to receive SMS text messages from SimpleTech regarding appointments, service requests, customer support, and account notifications at the mobile number provided. Message frequency varies. Message and data rates may apply. Reply STOP to opt out or HELP for help. Consent is not a condition of purchase. See our{" "}
            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-white">Privacy Policy</a>{" "}
            and{" "}
            <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-white">Terms and Conditions</a>.
          </span>
        </label>
      </div>

      <button
        type="submit"
        disabled={status === "submitting"}
        className="w-full bg-sky-400 hover:bg-sky-500 disabled:opacity-60 disabled:cursor-not-allowed text-white font-extrabold tracking-wider px-6 py-4 rounded-md transition-colors"
      >
        {status === "submitting" ? "SENDING..." : "SUBMIT"}
      </button>

      <p className="text-xs text-white/60 text-center pt-2">
        <a href="/privacy" className="underline hover:text-white">Privacy Policy</a>
        {" | "}
        <a href="/terms" className="underline hover:text-white">Terms of Service</a>
      </p>
    </form>
  );
}


